import create from 'zustand';

type NewContentAction = 'copy' | 'reuse';

interface IProgramContentVariables {
  reuseLessonIds: number[];
  copyLessonIds: number[];
  reuseUnitIds: number[];
  copyUnitIds: number[];
  addLessonId: (type: NewContentAction, id: number) => void;
  addUnitId: (type: NewContentAction, id: number) => void;
  removeLessonId: (type: NewContentAction, id: number) => void;
  removeUnitId: (type: NewContentAction, id: number) => void;
  anyLessonsChecked: (lessonIds: number[]) => boolean;
  allLessonsChecked: (lessonIds: number[]) => boolean;
  toggleAllLessons: (lessonIds: number[]) => void;
  toggleLessonOff: (lessonId: number) => void;
  toggleLessonOn: (lessonId: number) => void;
  toggleUnitOff: (unitId: number) => void;
  toggleUnitOn: (unitId: number) => void;
  isAnyChecked: () => boolean;
  reset: () => void;
  parentLessonIds: number[];
  parentUnitIds: number[];
  setParentLessonIds: (lessonIds: number[]) => void;
  setParentUnitIds: (unitIds: number[]) => void;
}

const useProgramContentStore = create<IProgramContentVariables>((set, get) => ({
  addLessonId: (type, id) => {
    const lessonIds = new Set(get()[`${type}LessonIds`]);
    lessonIds.add(id);
    set({ [`${type}LessonIds`]: [...lessonIds] });
  },
  addUnitId: (type, id) => {
    const unitIds = new Set(get()[`${type}UnitIds`]);
    unitIds.add(id);
    set({ [`${type}UnitIds`]: [...unitIds] });
  },
  allLessonsChecked: (lessonIds: number[]) => {
    const { reuseLessonIds, copyLessonIds, parentLessonIds } = get();

    if (!lessonIds.length) return false;

    return lessonIds
      .filter(id => !parentLessonIds.includes(id))
      .every(id => [...reuseLessonIds, ...copyLessonIds].includes(id));
  },
  anyLessonsChecked: (lessonIds: number[]) => {
    const { reuseLessonIds, copyLessonIds } = get();

    if (!lessonIds.length) return false;
    if (reuseLessonIds.some(lessonId => lessonIds.includes(lessonId))) return true;

    return copyLessonIds.some(lessonId => lessonIds.includes(lessonId));
  },
  copyLessonIds: [],
  copyUnitIds: [],
  isAnyChecked: () => {
    const { reuseUnitIds, reuseLessonIds, copyLessonIds, copyUnitIds } = get();
    if (reuseUnitIds.length > 0) return true;
    if (reuseLessonIds.length > 0) return true;
    if (copyLessonIds.length > 0) return true;
    return copyUnitIds.length > 0;
  },
  parentLessonIds: [],
  parentUnitIds: [],
  removeLessonId: (type, id) => {
    const lessonIds = new Set(get()[`${type}LessonIds`]);
    lessonIds.delete(id);
    set({ [`${type}LessonIds`]: [...lessonIds] });
  },
  removeUnitId: (type, id) => {
    const unitIds = new Set(get()[`${type}UnitIds`]);
    unitIds.delete(id);
    set({ [`${type}UnitIds`]: [...unitIds] });
  },
  reset: () => set({
    copyLessonIds: [],
    copyUnitIds: [],
    reuseLessonIds: [],
    reuseUnitIds: [],
  }),
  reuseLessonIds: [],
  reuseUnitIds: [],
  setParentLessonIds: (lessonIds: number[]) => {
    set({ parentLessonIds: lessonIds });
  },
  setParentUnitIds: (unitIds: number[]) => {
    set({ parentUnitIds: unitIds });
  },
  toggleAllLessons: (lessonIds) => {
    const { allLessonsChecked, parentLessonIds, toggleLessonOff, toggleLessonOn } = get();

    const filteredLessonIds = lessonIds.filter(id => !parentLessonIds.includes(id));

    if (allLessonsChecked(filteredLessonIds)) {
      filteredLessonIds.forEach(lessonId => toggleLessonOff(lessonId));
    } else {
      filteredLessonIds.forEach(lessonId => toggleLessonOn(lessonId));
    }
  },
  toggleLessonOff: (lessonId) => {
    const { removeLessonId } = get();

    removeLessonId('copy', lessonId);
    removeLessonId('reuse', lessonId);
  },
  toggleLessonOn: (lessonId) => {
    const { addLessonId, parentLessonIds } = get();

    if (parentLessonIds.includes(lessonId)) return;

    addLessonId('reuse', lessonId);
  },
  toggleUnitOff: (unitId) => {
    const { removeUnitId } = get();

    removeUnitId('copy', unitId);
    removeUnitId('reuse', unitId);
  },
  toggleUnitOn: (unitId) => {
    const { addUnitId, parentUnitIds } = get();

    if (parentUnitIds.includes(unitId)) return;

    addUnitId('reuse', unitId);
  },
}));

export default useProgramContentStore;
